import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#07090E]/85 backdrop-blur-xl border-b border-white/10 py-2.5"
                    : "bg-transparent border-b border-transparent py-4"
            }`}
        >
            <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto flex items-center justify-between gap-4">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("#home")}
                    className="flex items-center gap-2.5 shrink-0 group"
                    aria-label="Animexa Tech Solutions — home"
                >
                    <img
                        src="/assets/logo-mark.png"
                        alt="Animexa logo"
                        className={`rounded-md transition-all duration-500 ${scrolled ? "h-8 w-8" : "h-10 w-10"}`}
                    />
                    <span className="leading-none text-left">
                        <span className="font-display font-extrabold tracking-wide text-white text-base sm:text-lg block">
                            ANIMEXA
                        </span>
                        <span className="font-jbmono text-[8px] sm:text-[9px] tracking-[0.32em] text-[#F5C518] block mt-0.5">
                            TECH SOLUTIONS
                        </span>
                    </span>
                </button>

                <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            onClick={() => go(l.id)}
                            className="text-sm text-slate-300 hover:text-[#00E5FF] transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#00E5FF] after:transition-all after:duration-300 hover:after:w-full"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="nav-cta-consultation"
                        onClick={() => go("#contact")}
                        className="hidden sm:inline-flex items-center rounded-full bg-[#F5C518] px-5 py-2.5 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-[#E5B50F] hover:shadow-[0_0_28px_-6px_rgba(245,197,24,0.6)] active:scale-95"
                    >
                        Get Free Consultation
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-white/10 text-white"
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-[#0A0E17]/97 backdrop-blur-2xl border-b border-white/10"
                        aria-label="Mobile"
                    >
                        <div className="px-6 py-5 flex flex-col gap-1">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                    initial={{ opacity: 0, x: -14 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    onClick={() => go(l.id)}
                                    className="text-left py-3 text-base text-slate-200 border-b border-white/5 last:border-0 hover:text-[#00E5FF] transition-colors"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <button
                                data-testid="nav-mobile-cta-consultation"
                                onClick={() => go("#contact")}
                                className="mt-4 rounded-full bg-[#F5C518] px-5 py-3 text-sm font-semibold text-[#07090E]"
                            >
                                Get Free Consultation
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
